import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import styled, { keyframes } from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';

/* ─── Normalize Moroccan Phone ───────────────────────────────────── */
const normalizeTele = (value: string): string => {
  let digits = value.replace(/[\s+-]/g, '').replace(/[^\d]/g, '');
  if (/^0\d{9}$/.test(digits)) return '212' + digits.substring(1);
  if (/^212\d{9}$/.test(digits)) return digits;
  return digits;
};

/* ─── Props ──────────────────────────────────────────────────────── */
interface Props {
  Tele: string;
  setTele: (value: string) => void;
  Password: string;
  setPassword: (value: string) => void;
  handleSubmit: (e: React.FormEvent) => void;
  ShowEreurPopup: boolean;
  setShowEreurPopup: (value: boolean) => void;
}

/* ─── Component ──────────────────────────────────────────────────── */
const ConnectionVisiteur: React.FC<Props> = ({
  ShowEreurPopup,
  Tele,
  setTele,
  Password,
  setPassword,
  handleSubmit,
}) => {
  const [showPass, setShowPass] = useState(false);

  const handleTeleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setTele(normalizeTele(e.target.value));
  };

  return (
    <PageWrapper>
      <Blob className="blob blob--a" />
      <Blob className="blob blob--b" />

      <motion.div
        className="card"
        initial={{ opacity: 0, y: 40, scale: 0.97 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
      >
        {/* ── Header ── */}
        <div className="card__header">
          <motion.div
            className="badge"
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ delay: 0.25, type: 'spring', stiffness: 260, damping: 20 }}
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2">
              <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
              <path d="M7 11V7a5 5 0 0 1 10 0v4" />
            </svg>
          </motion.div>

          <motion.h1
            className="card__title"
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2, duration: 0.5 }}
          >
            Connexion
          </motion.h1>

          <motion.p
            className="card__sub"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.35 }}
          >
            Connectez-vous pour accéder à votre espace.
          </motion.p>
        </div>

        {/* ── Form ── */}
        <form className="card__form" onSubmit={handleSubmit} noValidate>

          {/* Phone */}
          <motion.div
            className="field"
            initial={{ opacity: 0, x: -18 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.45, delay: 0.2 }}
          >
            <input
              type="text"
              className="field__input"
              value={Tele}
              onChange={handleTeleChange}
              placeholder=" "
              required
            />
            <label className="field__label">
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" style={{ marginRight: 5 }}>
                <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 4.69 12 19.79 19.79 0 0 1 1.61 3.18 2 2 0 0 1 3.6 1h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L7.91 8.59a16 16 0 0 0 6 6l.96-.96a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 21.73 16z" />
              </svg>
              Téléphone
            </label>
            <span className="field__line" />
          </motion.div>

          {/* Password */}
          <motion.div
            className="field"
            initial={{ opacity: 0, x: -18 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.45, delay: 0.28 }}
          >
            <input
              type={showPass ? 'text' : 'password'}
              className="field__input"
              value={Password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder=" "
              required
            />
            <label className="field__label">
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" style={{ marginRight: 5 }}>
                <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                <path d="M7 11V7a5 5 0 0 1 10 0v4" />
              </svg>
              Mot de passe
            </label>
            <span className="field__line" />
            <button
              type="button"
              className="eye-btn"
              onClick={() => setShowPass((p) => !p)}
              aria-label="Afficher le mot de passe"
            >
              {showPass ? (
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M17.94 17.94A10.07 10.07 0 0112 20c-7 0-11-8-11-8a18.45 18.45 0 015.06-5.94" />
                  <path d="M9.9 4.24A9.12 9.12 0 0112 4c7 0 11 8 11 8a18.5 18.5 0 01-2.16 3.19" />
                  <line x1="1" y1="1" x2="23" y2="23" />
                </svg>
              ) : (
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                  <circle cx="12" cy="12" r="3" />
                </svg>
              )}
            </button>
          </motion.div>

          {/* Forgot password */}
          <motion.div
            className="forgot"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.35 }}
          >
            <Link to="/mot_de_passe_oublie" className="forgot__link">
              Mot de passe oublié ?
            </Link>
          </motion.div>

          {/* Submit */}
          <motion.button
            type="submit"
            className="btn-submit"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4, duration: 0.4 }}
            whileHover={{ scale: 1.025, boxShadow: '0 8px 32px rgba(212,175,55,0.35)' }}
            whileTap={{ scale: 0.97 }}
          >
            <span>Se connecter</span>
            <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.3">
              <path d="M15 3h4a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2h-4" />
              <polyline points="10 17 15 12 10 7" />
              <line x1="15" y1="12" x2="3" y2="12" />
            </svg>
          </motion.button>

          {/* Error feedback */}
          <AnimatePresence>
            {ShowEreurPopup && (
              <motion.div
                className="feedback"
                initial={{ opacity: 0, y: -8 }}
                animate={{ opacity: 1, y: 0, x: [0, -8, 8, -6, 6, 0] }}
                exit={{ opacity: 0, y: -8 }}
                transition={{ duration: 0.45 }}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                  <circle cx="12" cy="12" r="10" />
                  <line x1="12" y1="8" x2="12" y2="12" />
                  <line x1="12" y1="16" x2="12.01" y2="16" />
                </svg>
                <span>Téléphone ou mot de passe incorrect. Veuillez réessayer.</span>
              </motion.div>
            )}
          </AnimatePresence>
        </form>

        {/* ── Footer ── */}
        <motion.div
          className="card__divider"
          initial={{ scaleX: 0 }}
          animate={{ scaleX: 1 }}
          transition={{ delay: 0.5, duration: 0.5 }}
        />
        <motion.p
          className="card__footer"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.55 }}
        >
          Pas encore de compte ?{' '}
          <Link to="/créer_compte" className="card__link">
            Créer un compte
          </Link>
        </motion.p>
      </motion.div>
    </PageWrapper>
  );
};

/* ─── Keyframes ──────────────────────────────────────────────────── */
const floatA = keyframes`
  0%, 100% { transform: translate(0, 0) scale(1); }
  50%       { transform: translate(30px, -40px) scale(1.08); }
`;
const floatB = keyframes`
  0%, 100% { transform: translate(0, 0) scale(1); }
  50%       { transform: translate(-25px, 30px) scale(1.05); }
`;

/* ─── Styled Components ──────────────────────────────────────────── */
const Blob = styled.div``;

const PageWrapper = styled.div`
  @import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:wght@400;600;700&family=DM+Sans:wght@300;400;500&display=swap');

  --gold:       #d4af37;
  --gold-light: #f0d060;
  --gold-dim:   rgba(212,175,55,0.15);
  --navy:       #0b0f1e;
  --navy-card:  #111827;
  --navy-field: #1a2236;
  --text:       #e8eaf0;
  --text-muted: #8892a4;
  --error:      #f87171;
  --radius-lg:  20px;
  --radius-sm:  10px;

  font-family: 'DM Sans', sans-serif;
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 40px 16px;
  position: relative;
  overflow: hidden;

  .blob {
    position: fixed;
    border-radius: 50%;
    filter: blur(90px);
    pointer-events: none;
    z-index: 0;
  }
  .blob--a {
    width: 500px; height: 500px;
    background: radial-gradient(circle, rgba(212,175,55,0.11) 0%, transparent 70%);
    top: -100px; left: -150px;
    animation: ${floatA} 14s ease-in-out infinite;
  }
  .blob--b {
    width: 460px; height: 460px;
    background: radial-gradient(circle, rgba(59,130,246,0.07) 0%, transparent 70%);
    bottom: -90px; right: -130px;
    animation: ${floatB} 18s ease-in-out infinite;
  }
  /* ── Card ── */
  .card {
    position: relative;
    z-index: 1;
    width: 100%;
    max-width: 440px;
    background: var(--navy-card);
    border: 1px solid rgba(212,175,55,0.18);
    border-radius: var(--radius-lg);
    padding: 40px 44px 36px;
    box-shadow: 0 24px 80px rgba(0,0,0,0.55), 0 0 0 1px rgba(255,255,255,0.03);
    @media (max-width: 480px) { padding: 28px 20px 24px; }
  }
  /* ── Header ── */
  .card__header {
    text-align: center;
    margin-bottom: 32px;
  }
  .badge {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 52px; height: 52px;
    background: var(--gold-dim);
    border: 1px solid rgba(212,175,55,0.35);
    border-radius: 14px;
    color: var(--gold);
    margin-bottom: 16px;
  }
  .card__title {
    font-family: 'Cormorant Garamond', Georgia, serif;
    font-size: 2.1rem;
    font-weight: 700;
    color: var(--text);
    letter-spacing: 0.01em;
    margin: 0 0 6px;
    line-height: 1.15;
  }

  .card__sub {
    font-size: 0.88rem;
    color: var(--text-muted);
    margin: 0;
    line-height: 1.5;
  }

  /* ── Form ── */
  .card__form {
    display: flex;
    flex-direction: column;
    gap: 18px;
  }

  /* ── Field ── */
  .field {
    position: relative;
  }

  .field__input {
    width: 100%;
    background: var(--navy-field);
    border: 1px solid rgba(255,255,255,0.08);
    border-radius: var(--radius-sm);
    padding: 22px 42px 8px 14px;
    font-family: 'DM Sans', sans-serif;
    font-size: 0.93rem;
    color: var(--text);
    outline: none;
    transition: border-color 0.25s, box-shadow 0.25s;
    box-sizing: border-box;

    &:focus {
      border-color: var(--gold);
      box-shadow: 0 0 0 3px rgba(212,175,55,0.12);
    }
  }

  .field__label {
    position: absolute;
    left: 14px;
    top: 50%;
    transform: translateY(-50%);
    font-size: 0.88rem;
    color: var(--text-muted);
    pointer-events: none;
    display: flex;
    align-items: center;
    transition: top 0.22s ease, font-size 0.22s ease, color 0.22s ease, transform 0.22s ease;
    white-space: nowrap;
  }

  .field__input:focus + .field__label,
  .field__input:not(:placeholder-shown) + .field__label {
    top: 10px;
    transform: none;
    font-size: 0.72rem;
    color: var(--gold);
    font-weight: 500;
  }

  .field__line {
    position: absolute;
    bottom: 0; left: 14px; right: 14px;
    height: 2px;
    background: var(--gold);
    border-radius: 2px;
    transform: scaleX(0);
    transform-origin: left;
    transition: transform 0.3s ease;
  }
  .field__input:focus ~ .field__line { transform: scaleX(1); }

  /* ── Eye button ── */
  .eye-btn {
    position: absolute;
    right: 12px;
    top: 50%;
    transform: translateY(-50%);
    background: none;
    border: none;
    color: var(--text-muted);
    cursor: pointer;
    padding: 4px;
    display: flex;
    align-items: center;
    transition: color 0.2s;
    &:hover { color: var(--gold); }
  }

  /* ── Forgot ── */
  .forgot {
    text-align: right;
    margin-top: -6px;
  }
  .forgot__link {
    font-size: 0.78rem;
    color: var(--text-muted);
    text-decoration: none;
    transition: color 0.2s;
    &:hover { color: var(--gold); }
  }

  /* ── Submit ── */
  .btn-submit {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 10px;
    width: 100%;
    padding: 14px 24px;
    background: linear-gradient(135deg, #d4af37 0%, #f0d060 50%, #c8990a 100%);
    border: none;
    border-radius: var(--radius-sm);
    font-family: 'DM Sans', sans-serif;
    font-size: 0.95rem;
    font-weight: 500;
    color: #0b0f1e;
    cursor: pointer;
    letter-spacing: 0.03em;
    transition: filter 0.2s;

    &:hover { filter: brightness(1.06); }
    svg { transition: transform 0.2s; }
    &:hover svg { transform: translateX(3px); }
  }

  /* ── Feedback ── */
  .feedback {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 12px 16px;
    border-radius: var(--radius-sm);
    font-size: 0.84rem;
    line-height: 1.5;
    background: rgba(248,113,113,0.08);
    border: 1px solid rgba(248,113,113,0.3);
    color: #fca5a5;
  }

  /* ── Divider ── */
  .card__divider {
    height: 1px;
    background: rgba(255,255,255,0.06);
    margin: 24px 0 0;
    transform-origin: left;
  }

  /* ── Footer ── */
  .card__footer {
    text-align: center;
    margin: 16px 0 0;
    font-size: 0.83rem;
    color: var(--text-muted);
  }

  .card__link {
    color: var(--gold);
    text-decoration: none;
    font-weight: 500;
    transition: color 0.2s;
    &:hover { color: var(--gold-light); text-decoration: underline; }
  }
`;

export default ConnectionVisiteur;